import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Chip, IconButton, Popover, Typography } from '@mui/material';
import {
  Add,
  Warning,
  Error as ErrorIcon,
  HelpOutline,
} from '@mui/icons-material';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import {
  WorkersV2API,
  ConsolidatedSkill,
  ConsolidatedSkillsResponse,
} from '@/services/workersV2API';
import AddSkillModal from './AddSkillModal';

type Props = {
  workerId: number;
};

type SectionConfig = {
  key: 'approved' | 'needs_review' | 'in_progress' | 'ineligible';
  label: string;
  borderColor: string;
  bgColor: string;
};

const SECTIONS: SectionConfig[] = [
  {
    key: 'approved',
    label: 'Approved',
    borderColor: '#A7F3D0',
    bgColor: '#ECFDF5',
  },
  {
    key: 'needs_review',
    label: 'Needs Review',
    borderColor: '#FDE68A',
    bgColor: '#FFFBEB',
  },
  {
    key: 'in_progress',
    label: 'In Progress',
    borderColor: '#93C5FD',
    bgColor: '#EFF6FF',
  },
  {
    key: 'ineligible',
    label: 'Ineligible',
    borderColor: '#FECACA',
    bgColor: '#FEF2F2',
  },
];

const SkillChip = ({
  skill,
  section,
  onClick,
}: {
  skill: ConsolidatedSkill;
  section: SectionConfig['key'];
  onClick: () => void;
}) => {
  const getChipContent = () => {
    switch (section) {
      case 'approved': {
        const jobsCount = skill.worker_skill?.jobs_worked ?? 0;
        return (
          <Stack gap={6} style={{ alignItems: 'center' }}>
            <span>{skill.skill_name}</span>
            {jobsCount > 0 && (
              <Badge label={String(jobsCount)} status="theme" />
            )}
          </Stack>
        );
      }
      case 'needs_review': {
        return (
          <Stack gap={6} style={{ alignItems: 'center' }}>
            <Warning sx={{ fontSize: 14, color: '#F59E0B' }} />
            <span>{skill.skill_name}</span>
          </Stack>
        );
      }
      case 'in_progress': {
        const progress = skill.requirements_progress;
        return (
          <Stack gap={6} style={{ alignItems: 'center' }}>
            <span>{skill.skill_name}</span>
            {progress && (
              <Small color="lighter">
                {progress.completed}/{progress.total}
              </Small>
            )}
          </Stack>
        );
      }
      case 'ineligible':
        return (
          <Stack gap={6} style={{ alignItems: 'center' }}>
            <ErrorIcon sx={{ fontSize: 14, color: '#EF4444' }} />
            <span>{skill.skill_name}</span>
          </Stack>
        );
    }
  };

  const borderColorMap: Record<string, string> = {
    approved: '#A7F3D0',
    needs_review: '#FDE68A',
    in_progress: '#93C5FD',
    ineligible: '#FECACA',
  };

  return (
    <Chip
      label={getChipContent()}
      onClick={onClick}
      variant="outlined"
      sx={{
        borderColor: borderColorMap[section],
        borderRadius: '8px',
        height: 'auto',
        py: 0.5,
        px: 0.5,
        cursor: 'pointer',
        '& .MuiChip-label': {
          padding: '4px 8px',
        },
        '&:hover': {
          backgroundColor: '#F3F4F6',
        },
      }}
    />
  );
};

const WorkerSkillsCard = ({ workerId }: Props) => {
  const navigate = useNavigate();
  const [data, setData] = useState<ConsolidatedSkillsResponse['data'] | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);

  const fetchSkills = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await WorkersV2API.getConsolidatedSkills(workerId);
      if (response.success) {
        setData(response.data);
      } else {
        setError('Failed to fetch worker skills');
      }
    } catch (err) {
      console.error('Error fetching consolidated skills:', err);
      setError('Failed to fetch worker skills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSkills();
  }, [workerId]);

  const handleViewSkill = (skillId: number) => {
    navigate(`/workers_v2/${workerId}/skill/${skillId}`);
  };

  const handleSkillAdded = (skillId: number) => {
    setShowAddModal(false);
    navigate(`/workers_v2/${workerId}/skill/${skillId}`);
  };

  const totalCount = data?.skills?.length ?? 0;

  if (loading) {
    return (
      <Card title="Skills">
        <Card.Section>
          <Stack style={{ justifyContent: 'center', padding: '20px' }}>
            <Small color="lighter">Loading...</Small>
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Skills">
        <Card.Section>
          <Stack style={{ justifyContent: 'center', padding: '20px' }}>
            <Small color="lighter">{error}</Small>
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  return (
    <>
      <Card
        title={`Skills (${totalCount})`}
        actionsDropdown={
          <Stack gap={8} style={{ alignItems: 'center' }}>
            <IconButton
              size="small"
              onClick={(e) => setHelpAnchor(e.currentTarget)}
            >
              <HelpOutline fontSize="small" />
            </IconButton>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => setShowAddModal(true)}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                minWidth: '140px',
                whiteSpace: 'nowrap',
              }}
            >
              Add Skill
            </Button>
          </Stack>
        }
      >
        {totalCount === 0 ? (
          <Card.Section>
            <Stack style={{ justifyContent: 'center', padding: '20px' }}>
              <Small color="lighter">No skills found</Small>
            </Stack>
          </Card.Section>
        ) : (
          <>
            {SECTIONS.map((section) => {
              const skills = data?.grouped?.[section.key] ?? [];
              if (skills.length === 0) return null;

              return (
                <Card.Section key={section.key}>
                  <Stack vertical gap={12}>
                    <Stack gap={8} style={{ alignItems: 'center' }}>
                      <Body weight="semibold">{section.label}</Body>
                      <Badge
                        label={String(skills.length)}
                        status={
                          section.key === 'approved'
                            ? 'theme'
                            : section.key === 'ineligible'
                              ? 'danger'
                              : section.key === 'needs_review'
                                ? 'warning'
                                : 'neutral'
                        }
                      />
                    </Stack>
                    <Stack
                      gap={8}
                      style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}
                    >
                      {skills.map((skill) => (
                        <SkillChip
                          key={skill.skill_id}
                          skill={skill}
                          section={section.key}
                          onClick={() => handleViewSkill(skill.skill_id)}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Card.Section>
              );
            })}
          </>
        )}
      </Card>

      <Popover
        open={!!helpAnchor}
        anchorEl={helpAnchor}
        onClose={() => setHelpAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div style={{ padding: '16px', maxWidth: '360px' }}>
          <Typography variant="subtitle2" gutterBottom>
            Skill Categories
          </Typography>
          <Typography variant="body2" paragraph style={{ marginBottom: 8 }}>
            <strong>Approved</strong> — Worker has been granted this skill and
            can claim jobs that require it. The number badge shows jobs worked.
          </Typography>
          <Typography variant="body2" paragraph style={{ marginBottom: 8 }}>
            <strong>Needs Review</strong> — Worker has the skill but something
            needs admin attention (pending certificates, uniforms, AI assessment
            for review, or skill expiring soon).
          </Typography>
          <Typography variant="body2" paragraph style={{ marginBottom: 8 }}>
            <strong>In Progress</strong> — Worker has a skill request and is
            working through the requirements. The fraction shows completed vs
            total requirements.
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 0 }}>
            <strong>Ineligible</strong> — Worker skill has been disabled by an
            admin. The worker cannot claim jobs for this skill.
          </Typography>
        </div>
      </Popover>

      <AddSkillModal
        open={showAddModal}
        workerId={workerId}
        onClose={() => setShowAddModal(false)}
        onSkillAdded={handleSkillAdded}
      />
    </>
  );
};

export default WorkerSkillsCard;
